import { useId } from "react";
import "./ari-orb.css";

/**
 * AriOrb — the single, unified ARI visual identity, used everywhere ARI appears.
 * A satin champagne-gold pearl with flat Saturn rings sweeping in front of and behind
 * the sphere, a soft levitating float, glow and reflection (4D depth).
 * Props: size, state ("idle" | "thinking" | "responding" | "insight" | "speaking"),
 * rings, particles, tint, still, testId.
 */
const PARTICLES = [
  { top: "6%", left: "50%" }, { top: "24%", left: "90%" },
  { top: "70%", left: "88%" }, { top: "92%", left: "46%" },
  { top: "68%", left: "8%" }, { top: "26%", left: "12%" },
];

const RINGS = [
  { rx: 86, ry: 12.5, w: 1.1, o: 0.95 },
  { rx: 80, ry: 11.4, w: 0.8, o: 0.7 },
  { rx: 74, ry: 10.4, w: 0.7, o: 0.55 },
  { rx: 67, ry: 9.4, w: 0.9, o: 0.85 },
  { rx: 61, ry: 8.5, w: 0.6, o: 0.45 },
];

const RingSet = ({ front, id }) => (
  <svg className={`ari-sphere__rings ${front ? "ari-sphere__rings--front" : ""}`} viewBox="0 0 180 100" preserveAspectRatio="none">
    {front && (
      <defs>
        <clipPath id={id}><rect x="0" y="52" width="180" height="48" /></clipPath>
      </defs>
    )}
    <g className="ari-sphere__ringtilt" clipPath={front ? `url(#${id})` : undefined}>
      {RINGS.map((r, i) => (
        <ellipse key={i} className="ari-r" cx="90" cy="52" rx={r.rx} ry={r.ry} strokeWidth={r.w} opacity={front ? r.o : r.o * 0.55} />
      ))}
      <ellipse className="ari-r-sweep" cx="90" cy="52" rx="86" ry="12.5" strokeWidth="1.6" pathLength="100" />
    </g>
  </svg>
);

export function AriOrb({
  size = 40,
  state = "idle",
  rings,
  particles,
  tint,
  className = "",
  style,
  testId = "ari-orb",
  still = false,
}) {
  const showRings = rings ?? size >= 20;
  const showParticles = particles ?? size >= 30;
  const showReflection = size >= 72;
  const clipId = `ari-ring-front-${useId().replace(/:/g, "")}`;

  return (
    <span
      className={`ari-sphere ${size < 48 ? "ari-sphere--compact" : ""} ${still ? "ari-sphere--still" : ""} ${className}`}
      data-ari-state={state}
      data-testid={testId}
      aria-hidden="true"
      style={{ width: size, height: size, ...(tint || {}), ...style }}
    >
      {showReflection && <span className="ari-sphere__reflection" />}
      <span className="ari-sphere__float">
        <span className="ari-sphere__glow" />
        {showRings && <RingSet id={clipId} />}
        <span className="ari-sphere__core" />
        <span className="ari-sphere__energy" />
        <span className="ari-sphere__spec" />
        {showRings && <RingSet front id={clipId} />}
        {showParticles && (
          <span className="ari-sphere__particles">
            {PARTICLES.map((p, i) => (
              <i key={i} style={{ top: p.top, left: p.left, opacity: 0.45 + (i % 3) * 0.18 }} />
            ))}
          </span>
        )}
      </span>
    </span>
  );
}

export default AriOrb;
